import React, { useEffect, useRef } from "react";
import { IMAGES } from "../images/images";
import { Button, Col, Container, Form, Image, Row } from "react-bootstrap";
import { Icon } from "@iconify/react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../common/Loader";
import AOS from "aos";
import { Link } from "react-router-dom";
import { BgTools, Img, Section, Txt, useCmsImage } from "../cms/Cms";
import HeroMockup, { AboutMockup, DownloadMockup } from "./HeroMockup";

/**
 * One step of « Comment ça marche » / « Vendre via Omnicave ». The whole card
 * reveals at once (no text moving inside it) and lifts on hover.
 */
const StepCard = ({ n, delay, icon, img, titleKey, title, textKey, text, arrow }) => (
  <Col lg={4} md={6} className="d-flex" data-aos="fade-up" data-aos-delay={delay}>
    <div className="howitwork__body">
      <div className="numberBox">
        <h4>{n}</h4>
      </div>
      <span className={`howitwork__icon${img ? " howitwork__icon--img" : ""}`}>
        {img ? <Img {...img} /> : <Icon icon={icon} width="38" height="38" />}
      </span>
      <h3>
        <Txt k={titleKey}>{title}</Txt>
      </h3>
      <p>
        <Txt k={textKey}>{text}</Txt>
      </p>
      {arrow && <Image className="arrowPos" src={IMAGES.Arrow_SVG} alt="" />}
    </div>
  </Col>
);

const Home = () => {
  // LOGICAL VARIABLES
  useEffect(() => {
    AOS.init({ once: true, duration: 750, easing: "ease-out-cubic", offset: 40 });
  }, []);

  const [email, setEmail] = React.useState("");
  const bannerBg = useCmsImage("banner_bg_img", IMAGES.BannerImg);
  const registerBg = useCmsImage("register_bg_img", IMAGES.Register_Img);
  // The banner shows an animated app mock-up by default; an image published
  // from the admin panel for « Phone image » replaces it.
  const bannerPhone = useCmsImage("banner_img", IMAGES.Bannerimg_Img);
  const customBannerPhone = bannerPhone !== IMAGES.Bannerimg_Img ? bannerPhone : null;
  const downloadPhone = useCmsImage("download_img", IMAGES.AppDownload_Img);
  const customDownloadPhone = downloadPhone !== IMAGES.AppDownload_Img ? downloadPhone : null;
  const aboutPhone = useCmsImage("about_img", IMAGES.About_Img);
  const customAboutPhone = aboutPhone !== IMAGES.About_Img ? aboutPhone : null;

  const [loading, setLoading] = React.useState(false);
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };
  const SendMail = async () => {
    if (!validateEmail(email)) {
      toast.error("S'il vous plaît, mettez une adresse email valide.");
      return;
    }

    try {
      setLoading(true);
      await fetch("https://omni-cave.com/backend/api/v1/auth/mail-send", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: email }),
      })
        .then((response) => response.json())
        .then((res) => {
          if (res.success) {
            setLoading(false);
            setEmail("");
            toast.success(
              `Vous vous êtes inscrit avec succès au lancement de notre application. Nous vous informerons lorsqu'elle sera prête à être téléchargée. Nous vous remercions pour l'attention que vous portez à OmniCave !`
            );
          } else {
            setLoading(false);
            toast.error(res.message);
          }
        });
    } catch (error) {
      setLoading(false);
      toast.error("L'inscription a échoué, veuillez réessayer.");
      console.error("Error:", error);
    }
  };

  const navbarToggleRef = useRef(null);
  const navbarCollapseRef = useRef(null);

  const scrollToSection = (id) => {
    document.getElementById(id).scrollIntoView({
      behavior: "smooth",
    });

    // Toggle the collapse state of the navbar
    if (
      navbarToggleRef.current &&
      navbarCollapseRef.current.classList.contains("show")
    ) {
      navbarToggleRef.current.click();
    }
  };

  return (
    <React.Fragment ref={navbarToggleRef}>
      <Section id="banner" label="Banner">
        <section
          className="bannerSection"
          style={{ backgroundImage: `url(${bannerBg})` }}
        >
          <BgTools k="banner_bg_img" src={IMAGES.BannerImg} label="Background image" slotWidth={1440} slotHeight={770} />
          <BgTools
            k="banner_img"
            src={IMAGES.Bannerimg_Img}
            label="Phone image (optional: replaces the animated phones)"
            slotWidth={1100}
            slotHeight={1115}
          />
          <Container>
            <Row className="align-items-center">
              <Col md={6} className="heroCopy">
                <div className="heroCopy__kicker hero-in" style={{ "--d": "0s" }}>
                  <i />
                  <Txt k="banner_kicker">Lancement imminent · 300 places</Txt>
                </div>
                <h1 className="hero-in" style={{ "--d": "0.12s" }}>
                  <Txt k="banner_title">Vendez et achetez vos</Txt> <br />{" "}
                  <span>
                    <Txt k="banner_title_highlight">vins entre particuliers, en toute sécurité</Txt>
                  </span>
                </h1>
                <div className="banner-intro hero-in" style={{ "--d": "0.26s" }}>
                  <p>
                    <Txt k="banner_intro">
                      OmniCave est la première plateforme dédiée à la vente de vins entre particuliers.
                      Paiement sécurisé via compte séquestre, suivi d’envoi avec Sendcloud et frais parmi les plus bas du
                      marché : échangez vos bouteilles en toute confiance.
                    </Txt>
                  </p>
                  <p>
                    <strong>
                      <Txt k="banner_offer_highlight">Offre limitée à 300 membres.</Txt>
                    </strong>{" "}
                    <Txt k="banner_offer_text">
                      Les premiers inscrits obtiennent un badge «&nbsp;Membre fondateur&nbsp;» et des frais de vente
                      gratuits à vie.
                    </Txt>
                  </p>
                </div>
                <button
                  type="button"
                  className="appComBox hero-in"
                  style={{ "--d": "0.4s" }}
                  onClick={() => scrollToSection("registeryourself")}
                >
                  <span className="appComBox__label">
                    <Txt k="banner_cta">Je rejoins les 300 membres fondateurs</Txt>
                  </span>
                  <span className="appComBox__pill">
                    <Txt k="banner_cta_pill">Gratuit, sans engagement.</Txt>
                  </span>
                </button>
                <div className="socialLogin banner-socialLogin hero-in" style={{ "--d": "0.52s" }}>
                  <Link
                    className="socialLogin-link"
                    to="/"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection("registeryourself");
                    }}
                  >
                    <Image src={IMAGES.Google_Img} alt="Google Play" />
                    <span className="socialLogin-soon">Coming soon</span>
                  </Link>
                  <Link
                    className="socialLogin-link"
                    to="/"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection("registeryourself");
                    }}
                  >
                    <Image src={IMAGES.AppStore_Img} alt="App Store" />
                    <span className="socialLogin-soon">Coming soon</span>
                  </Link>
                </div>
                <ul className="heroTrust hero-in" style={{ "--d": "0.64s" }}>
                  <li>
                    <Icon icon="mdi:shield-lock-outline" />
                    <Txt k="banner_trust1">Paiement sous séquestre</Txt>
                  </li>
                  <li>
                    <Icon icon="mdi:truck-fast-outline" />
                    <Txt k="banner_trust2">Suivi Sendcloud</Txt>
                  </li>
                  <li>
                    <Icon icon="mdi:percent-outline" />
                    <Txt k="banner_trust3">Frais parmi les plus bas</Txt>
                  </li>
                </ul>
              </Col>
              <Col md={6} className="heroVisual">
                {customBannerPhone ? (
                  <Image src={customBannerPhone} className="ban_img hero-pop" alt="" />
                ) : (
                  <HeroMockup />
                )}
              </Col>
            </Row>
          </Container>
        </section>
      </Section>

      <Section id="register" label="Sign-up">
        <section
          className="registerSection"
          style={{ backgroundImage: `url(${registerBg})` }}
          id="registeryourself"
        >
          <BgTools k="register_bg_img" src={IMAGES.Register_Img} label="Background image" slotWidth={1600} slotHeight={384} />
          <Container>
            <Row className="justify-content-center">
              <Col md={8}>
                <h2 data-aos="fade-up" data-aos-duration="1000">
                  <Txt k="register_title">Rejoignez les 300 membres fondateurs</Txt>
                </h2>
                <p data-aos="fade-up" data-aos-duration="1500">
                  <Txt k="register_text">
                    Inscrivez-vous pour tester l’application en avant-première, obtenir le badge ‘Membre fondateur’ et
                    bénéficier de frais de vente gratuits à vie.
                  </Txt>
                </p>
                <div
                  className="sendbTn"
                  data-aos="fade-up"
                  data-aos-duration="2000"
                >
                  <Form.Control
                    aria-label="Small"
                    aria-describedby="inputGroup-sizing-sm"
                    placeholder="Votre adresse email"
                    onChange={(text) => setEmail(text.target.value)}
                    value={email}
                  />
                  <Button type="button" onClick={() => SendMail()}>
                    {loading ? (
                      <Loader color="white" size={28} />
                    ) : (
                      <Txt k="register_button">{"Je m\u2019inscris"}</Txt>
                    )}
                  </Button>
                </div>
                <p className="text-center" data-aos="fade-up" data-aos-duration="1500" style={{ fontSize: '14px', backgroundColor: 'red', height: '0px' }}>
                  Aucune publicité, vous pouvez vous désinscrire à tout moment.
                </p>
              </Col>
            </Row>
          </Container>
        </section>
      </Section>
      <Section id="buy" label="How to buy">
        <section id="howitwork" className="commonpad howitwork">
          <Container>
            <Row>
              <Col lg={12} md={12} className="text-center">
                <h3 className="h3" data-aos="fade-up" data-aos-duration="1000">
                  <Txt k="buy_kicker">Comment ça marche</Txt>
                </h3>
                <h2 data-aos="fade-up" data-aos-duration="1500">
                  <Txt k="buy_title">Comment l'utiliser</Txt>
                </h2>
                <p data-aos="fade-up" data-aos-duration="2000">
                  <Txt k="buy_text">Laissez-vous portez par le fonctionnement intuitif OmniCave.</Txt>
                </p>
              </Col>
            </Row>
            <Row className="mt-5 g-4 g-xl-5 howitwork__row">
              <StepCard
                n="01"
                delay={0}
                icon="ic:baseline-search"
                titleKey="buy_step1_title"
                title="Rechercher et explorer les vins"
                textKey="buy_step1_text"
                text="Filtrez par région, appellation, millésime ou prix pour trouver la bouteille idéale dans les caves des particuliers."
                arrow
              />
              <StepCard
                n="02"
                delay={120}
                icon="streamline:payment-10-solid"
                titleKey="buy_step2_title"
                title="Acheter ou faire une offre"
                textKey="buy_step2_text"
                text="Validez l’achat au prix affiché ou faites une offre au vendeur. Le paiement est placé sur un compte séquestre sécurisé jusqu’à réception."
                arrow
              />
              <StepCard
                n="03"
                delay={240}
                icon="carbon:delivery"
                titleKey="buy_step3_title"
                title="Recevez votre commande"
                textKey="buy_step3_text"
                text="Le vendeur expédie la bouteille avec Sendcloud : vous suivez le colis en temps réel jusqu’à la livraison."
              />
            </Row>
          </Container>
        </section>
      </Section>
      <Section id="sell" label="How to sell">
        <section
          id="howitwork"
          className="commonpad howitwork howitwork--sell"
          style={{ backgroundColor: "#f8f8f8" }}
        >
          <Container>
            <Row>
              <Col lg={12} md={12} className="text-center">
                <h3 className="h3" data-aos="fade-up" data-aos-duration="1000">
                  <Txt k="sell_kicker">Comment ça marche</Txt>
                </h3>
                <h2 data-aos="fade-up" data-aos-duration="1500">
                  <Txt k="sell_title">Vendre via Omnicave</Txt>
                </h2>
                <p data-aos="fade-up" data-aos-duration="2000">
                  <Txt k="sell_text">
                    Débloquez une vente transparente grâce au fonctionnement
                    intuitif d’OmniCave.
                  </Txt>
                </p>
              </Col>
            </Row>
            <Row className="mt-5 g-4 g-xl-5 howitwork__row">
              <StepCard
                n="01"
                delay={0}
                img={{ k: "sell_step1_img", src: IMAGES.addUser, alt: "", label: "Step 1 icon", slotWidth: 150, slotHeight: 150 }}
                titleKey="sell_step1_title"
                title="Créer un compte vendeur"
                textKey="sell_step1_text"
                text="Créez votre profil en quelques minutes, ajoutez vos informations et préparez-vous à vendre les bouteilles de votre cave."
                arrow
              />
              <StepCard
                n="02"
                delay={120}
                img={{ k: "sell_step2_img", src: IMAGES.cellar, alt: "", label: "Step 2 icon", slotWidth: 150, slotHeight: 150 }}
                titleKey="sell_step2_title"
                title="Ajouter des vins à l'inventaire"
                textKey="sell_step2_text"
                text="Renseignez le domaine, le millésime, l’état de la bouteille, le prix souhaité et ajoutez quelques photos. Votre vin est visible par toute la communauté."
                arrow
              />
              <StepCard
                n="03"
                delay={240}
                img={{ k: "sell_step3_img", src: IMAGES.trade, alt: "", label: "Step 3 icon", slotWidth: 150, slotHeight: 150 }}
                titleKey="sell_step3_title"
                title="Vendre directement ou négocier"
                textKey="sell_step3_text"
                text="Recevez des demandes ou des offres, discutez avec l’acheteur puis validez la vente. Frais de vente très faibles – et gratuits à vie pour les membres fondateurs."
              />
            </Row>
          </Container>
        </section>
      </Section>
      <Section id="about" label="About us">
        <section className="commonpad aboutUs" id="aboutus">
          <Container>
            <Row className="align-items-center">
              <Col md={6} className="heroVisual">
                <BgTools
                  k="about_img"
                  src={IMAGES.About_Img}
                  label="Image (optional: replaces the animated app screens)"
                  slotWidth={970}
                  slotHeight={1200}
                />
                {customAboutPhone ? (
                  <Image
                    src={customAboutPhone}
                    alt=""
                    className="img-fluid cImg"
                    data-aos="fade-left"
                    data-aos-duration="1200"
                  />
                ) : (
                  <AboutMockup />
                )}
              </Col>
              <Col md={6}>
                <h3 className="h3" data-aos="fade-up" data-aos-duration="1000">
                  <Txt k="about_kicker">À PROPOS DE NOUS</Txt>
                </h3>
                <h2 data-aos="fade-up" data-aos-duration="1300">
                  <Txt k="about_title">
                    OmniCave est une application pensée pour les amateurs, collectionneurs et passionnés de vin.
                  </Txt>
                </h2>
                <div data-aos="fade-up" data-aos-duration="1600">
                  <p>
                    <Txt k="about_text">
                      {"Notre mission : rendre la vente de vins entre particuliers simple, transparente et sécurisée, sans passer par des intermédiaires coûteux.\nGrâce au paiement en compte séquestre, au suivi d’envoi avec Sendcloud et à des frais réduits, vous gardez le contrôle sur vos bouteilles tout en rejoignant une communauté d’amateurs éclairés."}
                    </Txt>
                  </p>
                  {/* <h6 className="aboutusHeading">Notre mission :</h6> */}
                  {/* <p>
                    Notre mission est de rendre le commerce du vin plus
                    transparent et accessible, tout en créant une véritable
                    communauté d’amateurs autour de cette passion commune.{" "}
                  </p> */}
                  {/* <p>
                    Faciliter la découverte et la vente de vins d'exception entre
                    particuliers. Nous créons une communauté où l'échange se fait
                    en toute confiance.{" "}
                  </p> */}
                  {/* <h6 className="aboutusHeading">Ce que nous offrons :</h6>
                  <p>
                    {" "}
                    Sécurité totale : Transactions protégées jusqu'à la réception
                    de votre bouteille.. <br /> <br /> Vins rares : Accédez à des
                    sélections uniques et introuvables ailleurs. <br />
                    <br /> Communauté passionnée : Partagez vos coups de cœur et
                    suivez vos vendeurs favoris.
                  </p>
                  <h6 className="aboutusHeading"> Pourquoi choisir OmniCave ?</h6>
                  <p>
                    Transparence et confiance : Vendeurs et acheteurs vérifiés.{" "}
                    <br /> <br /> Simplicité : Interface intuitive pour une
                    expérience fluide. <br /> <br />
                    Support :Une équipe dédiée pour vous accompagner.
                  </p> */}
                </div>
                {/* <p data-aos="fade-up" data-aos-duration="2000" className="mb-4">
                  Duis aute irure dolor in reprehenderit in voluptate velit esse
                  cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat
                  cupidatat non proident, sunt in culpa qui officia deserunt
                  mollit anim id est laborum.
                </p> */}

                {/* <Link className="btn btn-primary">Read More</Link> */}
              </Col>
            </Row>
          </Container>
        </section>
      </Section>

      <Section id="download" label="Mobile app">
        <section id="downloadapp" className="commonpad downloadApp">
          <Container>
            <Row className="align-items-center">
              <Col md={6}>
                <div className="downloadApp__content">
                  <h3 className="h3" data-aos="fade-up" data-aos-duration="1000">
                    <Txt k="download_kicker">Application mobile</Txt>
                  </h3>
                  <h2 data-aos="fade-up" data-aos-duration="1000">
                    <Txt k="download_title">{"L\u2019application Omnicave arrive bientôt"}</Txt>
                  </h2>
                  <p data-aos="fade-up" data-aos-duration="1000">
                    <Txt k="download_text">
                      Nous finalisons actuellement la dernière version de l’application. Inscrivez-vous dès maintenant pour
                      faire partie des 300 premiers testeurs et profiter de frais de vente gratuits à vie.
                    </Txt>
                  </p>

                  <button
                    type="button"
                    className="downloadApp__ctaBtn"
                    data-aos="fade-up"
                    data-aos-duration="1200"
                    onClick={() => scrollToSection("registeryourself")}
                  >
                    <Txt k="download_cta">Je deviens membre fondateur</Txt>
                  </button>

                  <div className="socialLogin downloadApp__stores">
                    <Link
                      className="socialLogin-link"
                      to="/"
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToSection("registeryourself");
                      }}
                    >
                      <Image src={IMAGES.Google_Img} alt="Google Play" />
                      <span className="socialLogin-soon">Coming soon</span>
                    </Link>
                    <Link
                      className="socialLogin-link"
                      to="/"
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToSection("registeryourself");
                      }}
                    >
                      <Image src={IMAGES.AppStore_Img} alt="App Store" />
                      <span className="socialLogin-soon">Coming soon</span>
                    </Link>
                  </div>
                </div>
              </Col>

              <Col md={6} className="heroVisual">
                <BgTools
                  k="download_img"
                  src={IMAGES.AppDownload_Img}
                  label="App image (optional: replaces the animated phones)"
                  slotWidth={1140}
                  slotHeight={1180}
                />
                {customDownloadPhone ? (
                  <Image
                    src={customDownloadPhone}
                    className="img-fluid"
                    alt=""
                    data-aos="zoom-in"
                    data-aos-duration="1500"
                  />
                ) : (
                  <DownloadMockup />
                )}
              </Col>
            </Row>
          </Container>
        </section>
      </Section>

      <ToastContainer />
    </React.Fragment>
  );
};

export default Home;
